import useTranslation from '../useTranslation';
import { useCallback, useState } from 'react';
import { finalizeParse, parseShapefile } from 'utils/shapefileParser';

import { DEFAULT_GAP_POLICY, DEFAULT_RATE_UNIT } from './constants';
import type {
    ColumnSelectedHandler,
    FilesSelectedHandler,
    GapPolicyChangeHandler,
    PrescriptionGapPolicy,
    ShapefileParseError,
    UploadState,
    UseDataParams,
    UseDataReturn
} from './types';

const isInvalidPolygonsError = (error: ShapefileParseError | null): boolean => {
    if (!error) {
        return false;
    }

    return error.kind === 'invalidPolygons' || error.kind === 'unsupportedCrs';
};

// Map a parser error to a user-visible string. Generic kinds collapse to the
// translation hook's parse-failed message; invalid-polygons / unsupported-crs
// surface through the warning modal instead, so we leave those out here.
const formatParseError = ({
    error,
    parseFailedMessage
}: {
    error: ShapefileParseError;
    parseFailedMessage: (message: string) => string;
}): string => {
    if (error.kind === 'missingFile') {
        return parseFailedMessage(`missing .${error.file} file`);
    }

    if (error.kind === 'emptyShapefile') {
        return parseFailedMessage('shapefile contains no features');
    }

    if (error.kind === 'shpjsFailure') {
        return parseFailedMessage(error.detail);
    }

    return parseFailedMessage('unknown error');
};

const useData = ({
    attachError,
    attachPrescription,
    detachPrescription,
    isAttachingPrescription,
    isDetachingPrescription,
    onReplan
}: UseDataParams): UseDataReturn => {
    const {
        ERROR_PARSE_FAILED,
        ERROR_SERVER_REJECTED,
        WARNING_INVALID_POLYGONS,
        WARNING_UNSUPPORTED_CRS
    } = useTranslation();
    const [uploadState, setUploadState] = useState<UploadState>({ kind: 'idle' });
    const [gapPolicy, setGapPolicy] = useState<PrescriptionGapPolicy>(DEFAULT_GAP_POLICY);

    const handleFilesSelected: FilesSelectedHandler = useCallback(async ({ files }) => {
        if (files.length === 0) {
            return;
        }

        setUploadState({ kind: 'parsing' });

        const zipFile = files.find(file => file.name.toLowerCase().endsWith('.zip'));
        const result = await parseShapefile({
            input: zipFile ? { zip: zipFile } : { files }
        });

        if (!result.ok) {
            setUploadState({ error: result.error, kind: 'rejected' });

            return;
        }

        setUploadState({ kind: 'columnSelection', staged: result.staged });
    }, []);

    const handleColumnSelected: ColumnSelectedHandler = useCallback(
        ({ rateColumn }) => {
            if (uploadState.kind !== 'columnSelection') {
                return;
            }

            setUploadState({ kind: 'validating' });

            const finalized = finalizeParse({
                rateColumn,
                staged: uploadState.staged
            });

            if (!finalized.ok) {
                setUploadState({ error: finalized.error, kind: 'rejected' });

                return;
            }

            attachPrescription({
                gapPolicy,
                rateUnit: DEFAULT_RATE_UNIT,
                zones: finalized.zones
            });
            // Trigger a noop replan so the planner re-runs against the new
            // zones — attachPrescription itself does not auto-replan.
            onReplan();
            setUploadState({ kind: 'idle' });
        },
        [attachPrescription, gapPolicy, onReplan, uploadState]
    );

    const handleCancelUpload = useCallback(() => {
        setUploadState({ kind: 'idle' });
    }, []);

    const handleWarningDismiss = useCallback(() => {
        setUploadState({ kind: 'idle' });
    }, []);

    const handleGapPolicyChange: GapPolicyChangeHandler = useCallback(({ gapPolicy: next }) => {
        setGapPolicy(next);
    }, []);

    const handleDetach = useCallback(() => {
        detachPrescription();
        onReplan();
    }, [detachPrescription, onReplan]);

    const isWarningOpen =
        uploadState.kind === 'rejected' && isInvalidPolygonsError(uploadState.error);
    const isColumnPickerOpen = uploadState.kind === 'columnSelection';
    const columns = uploadState.kind === 'columnSelection' ? uploadState.staged.columns : [];
    const isLocalBusy = uploadState.kind === 'parsing' || uploadState.kind === 'validating';
    const isProcessing = isLocalBusy || isAttachingPrescription || isDetachingPrescription;

    const parseErrorMessage =
        uploadState.kind === 'rejected' && !isInvalidPolygonsError(uploadState.error)
            ? formatParseError({
                  error: uploadState.error,
                  parseFailedMessage: ERROR_PARSE_FAILED
              })
            : null;
    const serverErrorMessage = attachError ? ERROR_SERVER_REJECTED(attachError) : null;
    const errorMessage = parseErrorMessage ?? serverErrorMessage;

    let warningMessage = '';

    if (uploadState.kind === 'rejected') {
        if (uploadState.error.kind === 'invalidPolygons') {
            warningMessage = WARNING_INVALID_POLYGONS(uploadState.error.count);
        } else if (uploadState.error.kind === 'unsupportedCrs') {
            warningMessage = WARNING_UNSUPPORTED_CRS(uploadState.error.detected);
        }
    }

    return {
        columns,
        errorMessage,
        gapPolicy,
        handleCancelUpload,
        handleColumnSelected,
        handleDetach,
        handleFilesSelected,
        handleGapPolicyChange,
        handleWarningDismiss,
        isColumnPickerOpen,
        isProcessing,
        isWarningOpen,
        warningMessage
    };
};

export default useData;
